<template lang="pug">
y-dialog(v-if="showDialog" v-model="showDialog" max-width="1000" fullscreen persistent fluid scrollable)
  v-card(height="90vh")
    v-card-title.px-6
      y-button(outlined prepend-icon="mdi-arrow-left" color="neutral400" icon-color="neutral700" icon-size="20" size="xs" unset-min-width button-class="px-1" @click="closeDialog")
      div.text-xl.font-semibold.ml-4(v-if="step") Visit information
      div.text-xl.font-semibold.ml-4(v-else) Patient information
      v-spacer
      y-button.mr-4(v-if="showSwitchOldVersion" label="Switch old version" outlined color="neutral300" text-color="neutral700" background-color="var(--v-neutral300)" append-icon="$yzi-repeat-01" icon-size="15" icon-color="neutral700" size="xxs" height="24" @click="openSwitchOldVersionDialog = true")
      template(v-if="step")
        y-button.mr-3(label="Cancel" text size="sm" unset-min-width button-class="px-3" @click="closeDialog")
        y-button(v-if="selectedRegistration" label="Save" size="sm" unset-min-width button-class="px-3" :loading="isLoading" @click="sendData")
        y-button(v-else :label="purpose === 'consultation' ? $t('send_to_waiting_area') : $t('go_to_invoice')" size="sm" unset-min-width button-class="px-3" :loading="isLoading" @click="sendData")
      template(v-else)
        y-button.mr-3(label="Cancel" text size="sm" unset-min-width button-class="px-3" @click="closeDialog")
        y-button(label="Continue" size="sm" unset-min-width button-class="px-3" :loading="isLoading" @click="continueRegistration")
    v-divider
    v-card-text.basic200.pa-6
      div.d-flex.justify-center.mt-16(v-if="initLoading || isFetching")
        v-progress-circular(:width="2" :size="50" color="primary" indeterminate)
      v-window(v-else v-model="step" touchless)
        v-window-item(:value="0")
          patient-form(v-if="!step" ref="patient" :patient-data="patient" :edit-mode="isEditPatient" @update="updatePatient" @loading="isLoading = $event")
        v-window-item(:value="1")
          registration-form(v-if="step" ref="registration" :selected-registration="selectedRegistration" :purpose-data.sync="purpose" :patient-data="patient" :appointment="appointment" :need-mark-as-arrived="needMarkAsArrived" :reservation-fulfill-item="reservationFulfillItem" @goback="editPatient" @loading="isLoading = $event" @update="closeAllDialog" @fulfilled="$emit('fulfilled')")
  //- dialogs
  switch-old-version-dialog(v-if="openSwitchOldVersionDialog" :open.sync="openSwitchOldVersionDialog" @downgraded="handleDowngraded")
</template>

<script>
import { DateTime } from 'luxon'
import { mapState, mapGetters, mapActions } from 'vuex'
import PatientForm from '@/components/clinic/registration/PatientForm.vue'
import RegistrationForm from '@/components/clinic/registration/RegistrationForm.vue'
import SwitchOldVersionDialog from '@/components/clinic/registration/SwitchOldVersionDialog.vue'

export default {
  components: { PatientForm, RegistrationForm, SwitchOldVersionDialog },
  props: {
    open: {
      type: Boolean,
      default: false
    },
    purposeData: {
      type: String,
      default: 'consultation',
      validator(value) {
        return ['consultation', 'otc'].includes(value)
      }
    },
    selectedRegistration: {
      default: null
    },
    existingPatient: {
      default: null
    },
    prefillData: {
      default: null
    },
    prefillApptData: {
      default: null
    },
    reservationFulfillItem: {
      type: Object,
      default: null
    }
  },
  data () {
    return {
      initLoading: true,
      isLoading: false,
      isFetching: false,
      isEditPatient: false,
      purpose: null,
      appointment: null,
      step: 0,
      patient: {},
      registration: {},
      openSwitchOldVersionDialog: false,
    }
  },
  computed: {
    ...mapState('user', ['member']),
    ...mapState('clinic', ['cmsOsV2Enabled']),
    showSwitchOldVersion () {
      if (this.cmsOsV2Enabled) return false
      // Hide for members created strictly after 25 Jun 2026 (i.e. 26 Jun onward)
      const created = this.member && this.member.created
      if (created && DateTime.fromISO(created) >= DateTime.fromISO('2026-06-26')) return false
      return true
    },
    ...mapGetters({
      company: 'getCompany',
      clinic: 'clinic/getClinic',
    }),
    showDialog: {
      get () {
        return this.open 
      },
      set (newValue) {
        this.$emit('update:open', newValue)
      }
    },
    needMarkAsArrived () {
      return Boolean(this.prefillApptData) ?? false
    },
  },
  created () {
    this.purpose = this.purposeData
  },
  mounted () {
    if (this.selectedRegistration) {
      this.fetchPatient(this.selectedRegistration.patient.id)
      this.step = 1
    } else if (this.existingPatient) {
      this.fetchPatient(this.existingPatient)
    } else if (this.prefillData) {
      this.setPrefillData()
    } else if (this.prefillApptData) {
      this.setPrefillApptData()
    } else {
      this.initLoading = false
    }
  },
  methods: {
    ...mapActions(['showMessage']),
    fetchPatient (id) {
      this.isFetching = true
      this.$axios.get(`/clinics/${this.company.slug}/patients/${id}`)
        .then(resp => {
          this.patient = resp.data
          this.fetchAppointment(id)
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
        .finally(() => {
          this.isFetching = false
          this.initLoading = false
        })
    },
    fetchAppointment (id) {
      if (this.prefillApptData) {
        this.$axios.get(`/clinics/${this.company.slug}/appointments/${this.prefillApptData.appointment}`)
          .then(resp => {
            this.appointment = resp.data
          })
          .catch(() => {})
        return
      }
      let params = {
        patient: id,
        statuses: 'new,processed',
        date: this.getTodayDate(),
        ordering: 'start'
      }
      this.$axios.get(`/clinics/${this.company.slug}/appointments/`, { params })
        .then(resp => {
          if (resp.data.results.length > 0) {
            this.appointment = resp.data.results[0]
          }
        })
        .catch(() => {})
    },
    setPrefillData () {
      let data = this.prefillData
      let patient = {
        ic: data.ic,
        name: data.name,
        dob: this.getDOBFromIC(data.ic),
        gender: data.gender === 'L' ? 'male' : 'female',
        nationality: 'Malaysia',
        race: data.race?.toLowerCase() || null,
        religion: data.religion?.toLowerCase() || null,
      }
      if (data.address) {
        patient.address = { ...data.address, country: 'Malaysia' }
      }
      if (this.prefillData.photo) {
        patient.prefill_photo = this.prefillData.photo
      }
      this.patient = patient
      this.initLoading = false
    },
    setPrefillApptData () {
      let data = this.prefillApptData
      let patient = {
        ic: data.patient.ic || null,
        name: data.patient.name || null,
        phone: data.patient.phone || null,
        email: data.patient.email || null,
        gender: data.patient.gender || null,
        nationality: data.patient.nationality || null,
        race: data.patient.race || null,
        religion: data.patient.religion || null,
        dob: this.getDOBFromIC(data.patient.ic)
      }
      if (data.address) {
        patient.address = { ...data.address }
      }
      if (data.patient.profile_image?.file) {
        patient.photo = data.patient.profile_image.file
      }
      this.patient = patient
      this.$set(this.registration, 'provider', this.prefillApptData.provider)
      this.initLoading = false
    },
    editPatient () {
      this.step = 0
      this.isEditPatient = true
    },
    updatePatient (id) {
      this.fetchPatient(id)
      this.step = 1
      this.isEditPatient = false
      this.$emit('update:existingPatient', id)
    },
    continueRegistration () {
      this.$refs.patient.saveData()
    },
    sendData () {
      this.$refs.registration.saveData()
    },
    closeDialog () {
      this.$emit('closed')
      this.showDialog = false
    },
    closeAllDialog () {
      this.$emit('updated')
      this.closeDialog()
    },
    getTodayDate () {
      const today = new Date()
      const year = today.getFullYear()
      const month = (today.getMonth() + 1).toString().padStart(2, '0')
      const day = today.getDate().toString().padStart(2, '0')
      return `${year}-${month}-${day}`
    },
    getDOBFromIC(icNumber) {
      // Removing non-digit characters from the IC number
      const cleanICNumber = icNumber.replace(/\D/g, '');
      
      if (cleanICNumber.length === 12 && /^[0-9]+$/.test(cleanICNumber)) {
        // Extracting birth date information
        const year = cleanICNumber.substring(0, 2);
        const month = cleanICNumber.substring(2, 4);
        const day = cleanICNumber.substring(4, 6);

        // Adjusting the year for the 21st century
        const currentYear = new Date().getFullYear();
        let century = 1900
        if (year <= Number(currentYear.toString().substring(2))) {
          century = 2000
        }
        const adjustedYear = parseInt(year, 10) + century;
        const finalYear = adjustedYear > currentYear ? adjustedYear - 100 : adjustedYear;

        // Formatting the date
        const formattedDOB = `${finalYear}-${month}-${day}`;
        const regex = /^(?:(?:19|20)\d\d)-(?:0[1-9]|1[0-2])-(?:0[1-9]|[12][0-9]|3[01])$/
        if (regex.test(formattedDOB)) {
          return formattedDOB;
        }
      }
      return null
    },
    handleDowngraded () {
      this.$emit('downgraded')
    }
  }
}
</script>

<style lang="scss" scoped>
.file-change-layout {
  position: relative;
}
.input-change-layout {
  position: absolute;
  top: 0px;
  left: 0px;
  right: 0px;
  bottom: 0px;
  opacity: 0;
}
::v-deep {
  .v-text-field--outlined {
    fieldset {
      border-color: var(--v-neutral400);
    }
  } 
  .error--text {
    color: var(--v-danger500) !important;
    fieldset {
      border-color: var(--v-danger500);
    }
  }
}
</style>

<i18n>
{
  "en": {
    "address_1": {
      "label": "Address Line 1",
      "placeholder": "e.g. K-3-01, Conezion, Persiaran IRC3"
    },
    "address_2": {
      "label": "Address Line 2",
      "placeholder": "e.g. IOI Resort City"
    },
    "postcode": {
      "label": "Postcode",
      "placeholder": "e.g. 62502"
    },
    "city": {
      "label": "City",
      "placeholder": "e.g. Putrajaya"
    },
    "state": {
      "label": "State",
      "placeholder": "e.g. Wilayah Persekutuan Putrajaya"
    },
    "country": {
      "label": "Country",
      "placeholder": "e.g. Malaysia"
    }
  },
  "id": {
   "address_1": {
      "label": "Nama Jalan, Gedung, No Rumah, RT/RW, Kelurahan",
      "placeholder": "e.g. Jalan Prof. Joko Sutono Sh No.999, RT.14/RW.6, Petogogan, "
    },
    "address_2": {
      "label": "Kecamatan",
      "placeholder": "Kec. Kby. Baru"
    },
    "postcode": {
      "label": "Kode Pos",
      "placeholder": "12160"
    },
    "city": {
      "label": "Kota",
      "placeholder": "Kota Jakarta Selatan"
    },
    "state": {
      "label": "Provinsi",
      "placeholder": "DKI Jakarta"
    },
    "country": {
      "label": "Country",
      "placeholder": "e.g. Indonesia"
    }
  }
}
</i18n>